import { tick } from "svelte";
import { init } from "svelte/internal";
export const FOCUSABLE_ELEMENTS = [
  "a[href]",
  "area[href]",
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  "select:not([disabled]):not([aria-hidden])",
  "textarea:not([disabled]):not([aria-hidden])",
  "button:not([disabled]):not([aria-hidden])",
  "iframe",
  "object",
  "embed",
  "[contenteditable]",
  '[tabindex]:not([tabindex^="-"])', // Include elements with positive tabindex
];

export function focusTrap(
  node,
  { focusableSelectors = FOCUSABLE_ELEMENTS.join(",") } = {}
) {
  let firstFocusableElement;
  let lastFocusableElement;
  let focusableElements;
  let observer;

  async function initializeFocusTrap() {
    await tick();
    observer = new MutationObserver((mutations) => {
      // Only update if content was added or removed
      if (mutations.some(m => m.addedNodes.length > 0 || m.removedNodes.length > 0)) {
        updateFocusableElements();
      }
    });

    observer.observe(node, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-hidden', 'tabindex'] 
    });
    updateFocusableElements();
  }

  function updateFocusableElements() {
    // Get all focusable elements and filter out the ones that are disabled or hidden
    focusableElements = Array.from(
      node.querySelectorAll(focusableSelectors)
    ).filter(
      (el) => {
        const style = window.getComputedStyle(el);
        return !el.hasAttribute("disabled") && 
               !el.hasAttribute("aria-hidden") &&
               style.display !== 'none' &&
               style.visibility !== 'hidden';
      }
    );

    // Sort by tabindex, preserving DOM order for elements with same tabindex
    focusableElements.sort((a, b) => {
      const aIndex = a.getAttribute("tabindex");
      const bIndex = b.getAttribute("tabindex");
      if (aIndex === null && bIndex === null) return 0;
      if (aIndex === null) return 1;
      if (bIndex === null) return -1;
      return parseInt(aIndex) - parseInt(bIndex);
    });

    firstFocusableElement = focusableElements[0];
    lastFocusableElement = focusableElements[focusableElements.length - 1];
  }

  function handleKeyDown(event) {
    if (event.key !== "Tab") return;

    // Always update focusable elements before handling tab
    updateFocusableElements();

    if (!focusableElements.length) return;

    const currentElement = document.activeElement;
    const currentIndex = focusableElements.indexOf(currentElement);
    
    if (event.shiftKey) {
      // If shift+tab
      if (currentIndex <= 0) {
        event.preventDefault();
        lastFocusableElement?.focus();
      } else if (currentIndex === -1) {
        event.preventDefault();
        lastFocusableElement?.focus();
      }
    } else {
      // If tab
      if (currentIndex === focusableElements.length - 1) {
        event.preventDefault();
        firstFocusableElement?.focus();
      } else if (currentIndex === -1) {
        event.preventDefault();
        firstFocusableElement?.focus();
      }
    }
  }

  initializeFocusTrap();
  node.addEventListener("keydown", handleKeyDown);
  
  return {
    update(newOptions) {
      if (
        newOptions.focusableSelectors &&
        newOptions.focusableSelectors !== focusableSelectors
      ) {
        focusableSelectors = newOptions.focusableSelectors;
        updateFocusableElements();
      }
    },
    destroy() {
      observer?.disconnect();
      node.removeEventListener("keydown", handleKeyDown);
    },
  };
}


/**
 * Fix the font family to be a valid CSS font family
 * @param fontFamily - The font family to fix
 * @returns A valid CSS font family
 */
export function fixFontFamily(fontFamily) {
  // split fontFamily into font names
  const fontNames = fontFamily.split(',').map(name => name.trim().replace(/['"]/g, ''));
  // remove duplicates
  const uniqueFontNames = [...new Set(fontNames)];
  // add quotes around the font names if they require it
  const fixedFontNames = uniqueFontNames.map(name => name.includes(' ') ? `"${name}"` : name);
  return fixedFontNames.join(',');
}